import { gql } from "@apollo/client";
import {
  DataConnectionId,
  DataSourceFilter,
  OrgRole,
  convertDeprecatedDataSourceFilter,
  guardNever,
} from "@hex/common";
import { useMemo } from "react";

import { useCurrentUser } from "../../../../hooks/me/useCurrentUser";
import { ORG_ID } from "../../../../orgs";

import {
  useGetThreadsSettingsForEditorQuery,
  useGetThreadsSettingsForExplorerQuery,
} from "./useGetThreadsSettings.generated";

gql`
  query GetThreadsSettingsForExplorer($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      defaultDataConnection {
        id
      }
      exploreAgentSettings {
        id
        explorerEnabled
        explorerDataSource
        explorerDefaultDataConnection {
          id
        }
      }
    }
  }

  query GetThreadsSettingsForEditor($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      defaultDataConnection {
        id
      }
      exploreAgentSettings {
        id
        editorEnabled
        editorDataSource
        editorDefaultDataConnection {
          id
        }
      }
    }
  }
`;

const ROLE_MAP: Record<OrgRole, "EDITOR" | "EXPLORER" | null> = {
  [OrgRole.ADMIN]: OrgRole.EDITOR,
  [OrgRole.MANAGER]: OrgRole.EDITOR,
  [OrgRole.EDITOR]: OrgRole.EDITOR,
  [OrgRole.EXPLORER]: OrgRole.EXPLORER,
  [OrgRole.MEMBER]: null,
  [OrgRole.GUEST]: null,
  [OrgRole.EMBEDDED_USER]: null,
  [OrgRole.ANONYMOUS]: null,
};

export interface ThreadsSettings {
  enabledForRole: boolean;
  dataSource: DataSourceFilter;
  defaultDataConnectionId: DataConnectionId | null;
  orgConfiguredDefaultDataConnectionId: DataConnectionId | null;
}

export const useGetThreadsSettings = (): {
  loading: boolean;
  settings: ThreadsSettings | null;
} => {
  const user = useCurrentUser();
  const orgRole = user?.orgRole;

  const settingToLoad = orgRole ? ROLE_MAP[orgRole] : undefined;

  const { data: explorerSettingsData, loading: explorerSettingsLoading } =
    useGetThreadsSettingsForExplorerQuery({
      variables: { orgId: ORG_ID },
      skip: settingToLoad !== "EXPLORER",
      // A permissions error can occur if the explorer doesn't have access to the set
      // default data connection. If that's the case, we want to still use the
      // rest of the data returned.
      errorPolicy: "all",
    });

  const { data: editorSettingsData, loading: editorSettingsLoading } =
    useGetThreadsSettingsForEditorQuery({
      variables: { orgId: ORG_ID },
      skip: settingToLoad !== "EDITOR",
      errorPolicy: "all",
    });

  const result = useMemo(() => {
    if (
      orgRole == null ||
      orgRole === OrgRole.EMBEDDED_USER ||
      orgRole === OrgRole.ANONYMOUS ||
      orgRole === OrgRole.MEMBER ||
      orgRole === OrgRole.GUEST
    ) {
      return { loading: false, settings: null };
    }

    if (orgRole === OrgRole.ADMIN || orgRole === OrgRole.MANAGER) {
      if (editorSettingsLoading || editorSettingsData == null) {
        return { loading: editorSettingsLoading, settings: null };
      }

      const org = editorSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      const defaultDataConnectionId =
        settings.editorDefaultDataConnection?.id ??
        org?.defaultDataConnection?.id ??
        null;
      const editorDataSource = convertDeprecatedDataSourceFilter(
        settings.editorDataSource,
      );

      return {
        loading: false,
        settings: {
          enabledForRole: true,
          // Admins can always choose their data source but should default to the
          // editor configuration for consistency with the "restrict agent data
          // access" setting.
          dataSource: editorDataSource,
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else if (orgRole === OrgRole.EDITOR) {
      if (editorSettingsLoading || editorSettingsData == null) {
        return { loading: editorSettingsLoading, settings: null };
      }

      const org = editorSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      const defaultDataConnectionId =
        settings.editorDefaultDataConnection?.id ??
        org?.defaultDataConnection?.id ??
        null;

      return {
        loading: false,
        settings: {
          enabledForRole: settings.editorEnabled,
          dataSource: convertDeprecatedDataSourceFilter(
            settings.editorDataSource,
          ),
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else if (orgRole === OrgRole.EXPLORER) {
      if (explorerSettingsLoading || explorerSettingsData == null) {
        return { loading: explorerSettingsLoading, settings: null };
      }

      const org = explorerSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      const defaultDataConnectionId =
        settings.explorerDefaultDataConnection?.id ??
        org?.defaultDataConnection?.id ??
        null;

      return {
        loading: false,
        settings: {
          enabledForRole: settings.explorerEnabled,
          dataSource: convertDeprecatedDataSourceFilter(
            settings.explorerDataSource,
          ),
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else {
      guardNever(orgRole, orgRole);
      return { loading: false, settings: null };
    }
  }, [
    orgRole,
    editorSettingsLoading,
    editorSettingsData,
    explorerSettingsLoading,
    explorerSettingsData,
  ]);

  return result;
};
